import { useTranslation } from "react-i18next"

import { Close } from "@mui/icons-material"
import { Grid } from "@mui/material"
import { Button, InputText, Select, Typography } from "@synapse-analytics/synapse-ui"
import { capitalize } from "lodash"

import { SMALL_ICON_SIZE } from "../../Constants"
import type { TableFormEditorProps } from "../../Interfaces"
import { useSchemaForm } from "../../hooks/useSchemaForm"

import styles from "./TabularEditor.module.scss"

/**
 * Renders the header row for the tabular form editor
 * @returns {React.ReactElement} Grid component with column headers for Features, Type, and Value
 */
function TableHeader(): React.ReactElement {
  const { t } = useTranslation("predictions", {
    keyPrefix: "tabularEditor.headers",
  })

  return (
    <Grid item xs={12} className={styles.tableHead}>
      <Grid item xs={4}>
        <Typography variant="a">{t("features")}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="a">{t("type")}</Typography>
      </Grid>
      <Grid item xs={5}>
        <Typography variant="a">{t("value")}</Typography>
      </Grid>
    </Grid>
  )
}

/**
 * A tabular editor component for managing schema-based form data
 * @param {TableFormEditorProps} props - The component props
 * @param {Object} schema - The schema object containing feature definitions
 * @param {Function} setSchema - Function to update the schema
 * @param {number} rowsPerPage - Number of rows to display per page
 * @param {number} page - Current page number
 * @param {string} search - Search term to filter features
 * @returns {React.ReactElement} Grid container with editable schema features
 */
export function TabularEditor(props: Readonly<TableFormEditorProps>): React.ReactElement {
  const { schema, setSchema, rowsPerPage, page, search } = props
  const { handleInputChange, handleNameChange, handleTypeChange, handleRemoveFeature } = useSchemaForm(
    schema,
    setSchema,
  )

  const { t } = useTranslation("predictions", {
    keyPrefix: "tabularEditor",
  })

  const filteredFeatures = Object.keys(schema)
    // First, paginate the features by slicing based on current page and rows per page
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    // Then, filter features by search term if one exists
    // If search is empty, return all features (true)
    // If search exists, check if feature name includes search term (case-insensitive)
    .filter((feature) => (search ? schema[feature]?.name?.toLowerCase().includes(search.toLowerCase()) : true))

  return (
    <Grid item className={styles.table}>
      <TableHeader />

      {filteredFeatures.map((feature) => (
        <Grid key={schema[feature]?.id} item xs={12} className={styles.tableCells}>
          <Grid item xs={4} pr={1}>
            {schema[feature]?.new ? (
              <InputText
                hideDescription
                size="small"
                variant="filled"
                fullWidth
                value={schema[feature].name}
                handleChange={(e) => handleNameChange(e, feature)}
              />
            ) : (
              <Typography variant="p" noWrap>
                {schema[feature]?.name}
              </Typography>
            )}
          </Grid>

          <Grid item xs={3} pr={1} display="flex" gap={0.5} alignItems="center">
            {schema[feature] && (
              <Select
                size="small"
                value={capitalize(schema[feature].type)}
                fullWidth
                options={["Text", "Number", "Boolean", "Date", "List", "Undefined"]}
                menuProps={{ menuMaxContent: true }}
                handleChange={(e) => handleTypeChange(e, feature)}
              />
            )}
          </Grid>

          {(typeof schema[feature]?.value === "object" && schema[feature].value !== null) ||
          schema[feature]?.type === "LIST" ? (
            <Typography variant="a">
              {schema[feature].type === "LIST" ? t("dataView.cannotViewList") : t("dataView.cannotViewObject")}
            </Typography>
          ) : (
            <Grid item xs={5} display={"flex"} gap={0.5}>
              <InputText
                hideDescription
                size="small"
                variant="filled"
                fullWidth
                value={schema[feature]?.value as string | number | undefined}
                handleChange={(e) => handleInputChange(e, feature)}
                type={schema[feature]?.type === "NUMBER" ? "number" : "text"}
              />
              {schema[feature]?.new && (
                <Button size="small" onClick={() => handleRemoveFeature(feature)}>
                  <Close style={{ ...SMALL_ICON_SIZE, color: "var(--red-text-2)" }} />
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      ))}
    </Grid>
  )
}
